import { useTranslation } from 'react-i18next';
import { CreditCard, Landmark } from '@parrot-software/design-system/icons';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { PaymentMethodTypes } from '@subscriptions/domain/types/SubscriptionsConst';
import {
  ClabeDisplay,
  InfoItem,
  MissingDataBadge,
  PaymentAlertBadge,
} from '@subscriptions/presentation/components/base';
import { formatBrand, getLastPaymentErrorContent } from '@subscriptions/presentation/utils';

interface SubscriptionPaymentInfoProps {
  hasError?: boolean;
  lastError?: SubscriptionBlockViewDto['lastError'];
  paymentMethod: SubscriptionBlockViewDto['paymentMethod'];
}

/**
 * SubscriptionPaymentInfo - "Método de pago" column of the subscription block card.
 *
 * Branches strictly on the method type (the API populates `providerPublicId` for BOTH
 * methods — CLABE for SPEI, last-four for CARD — so the type, not that field, drives the UI):
 * - SPEI: "SPEI · CLABE Fintoc" title plus the formatted CLABE with its copy action.
 * - CARD: "Visa ····1234 vence MM/AA" on a single line (the expiration is dropped when the
 *   method does not expose one).
 *
 * The leading icon follows the method type (SPEI → bank, CARD → credit card), defaulting
 * to the credit card icon when the block has no payment method configured.
 *
 * The badge slot carries the payment error, labeled with the mapped last-payment-error
 * copy when a `lastError` exists and generic when only `hasError` is set.
 */
export const SubscriptionPaymentInfo = ({
  hasError = false,
  lastError,
  paymentMethod,
}: SubscriptionPaymentInfoProps) => {
  const { t, i18n } = useTranslation('subscriptions');

  const isSpei = paymentMethod?.methodType === PaymentMethodTypes.SPEI;
  const isCard = paymentMethod?.methodType === PaymentMethodTypes.CARD;

  // SPEI exposes its CLABE through providerPublicId; CARD never renders the CLABE.
  const hasClabe = isSpei && Boolean(paymentMethod?.providerPublicId);

  const icon = isSpei ? <Landmark className="h-4 w-4" /> : <CreditCard className="h-4 w-4" />;

  const renderStatusBadge = () => {
    if (lastError) return <PaymentAlertBadge label={getLastPaymentErrorContent(lastError, t, i18n).badgeText} />;
    if (hasError && paymentMethod) return <PaymentAlertBadge />;
    return null;
  };

  const cardTitle =
    paymentMethod?.brand && paymentMethod?.lastFourDigits
      ? t('paymentMethod.cardMasked', {
          brand: formatBrand(paymentMethod.brand),
          lastFourDigits: paymentMethod.lastFourDigits,
        })
      : t('paymentMethod.card');

  return (
    <InfoItem badge={renderStatusBadge()} icon={icon} label={t('paymentMethod.label')}>
      {!paymentMethod && <MissingDataBadge label={t('paymentMethod.noPaymentMethod')} />}

      {isSpei && (
        <>
          <p className="text-sm font-semibold leading-5 text-foreground break-words">
            {t('paymentMethod.speiTitle', { bank: t('paymentMethod.bankName') })}
          </p>
          {hasClabe && <ClabeDisplay providerPublicId={paymentMethod.providerPublicId} variant="flat" />}
        </>
      )}

      {isCard && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold leading-5 text-foreground break-words">{cardTitle}</p>
          {paymentMethod.expirationDate && (
            <p className="text-sm font-medium leading-5 text-foreground">
              {t('paymentMethod.expiresShort', { expiration: paymentMethod.expirationDate })}
            </p>
          )}
        </div>
      )}
    </InfoItem>
  );
};
